import * as Sentry from "@sentry/react";
import type { QueryClient } from "@tanstack/react-query";
import {
  getUserProfileUserProfileGet,
  getUserProfileUserProfileGetQueryKey,
  type User,
  useGetUserProfileUserProfileGet,
} from "kubb";
import {
  PERSISTED_QUERY_OPTIONS,
  reconcileCacheUser,
} from "lib/queryCachePersist";
import { useAuthState } from "reducers/auth";

export const PROFILE_STALE_TIME = 5 * 60 * 1000;

/**
 * Imperatively prime the TanStack cache with the current user, the source of
 * truth `useProfile` reads. `fetchQuery` awaits/throws, so callers' success and
 * sign-out branches are preserved (axios rejects non-2xx and the 401 interceptor
 * fires). Shared by `<AuthBootstrap>` (session restore) and Login (sign-in).
 *
 * Returns the user's id so callers can scope the persisted cache to one user.
 * Tags the query for persistence here too (not just on the `useProfileQuery`
 * observer) so the primed profile is snapshot-eligible immediately.
 */
export async function primeProfile(queryClient: QueryClient): Promise<string> {
  const d = await queryClient.fetchQuery({
    queryKey: getUserProfileUserProfileGetQueryKey(),
    queryFn: () => getUserProfileUserProfileGet(),
    staleTime: PROFILE_STALE_TIME,
    meta: { persist: true },
  });
  Sentry.setUser({ email: d.data.email_address });
  return d.data.user_id;
}

/**
 * Prime the profile and scope the persisted cache to the resolved user in one
 * step: if a different user's snapshot was hydrated, `reconcileCacheUser` drops
 * it and we re-prime the profile into the now-clean cache. Shared by
 * `<AuthBootstrap>` (session restore) and Login (sign-in) so the user-scoping
 * lives in exactly one place.
 */
export async function primeAndScopeProfile(
  queryClient: QueryClient,
): Promise<void> {
  const userId = await primeProfile(queryClient);
  if (reconcileCacheUser(userId)) {
    await primeProfile(queryClient);
  }
}

/**
 * The single shared current-user query. TanStack owns the data, invalidation and
 * refetch — exactly like the company/scheme lists (see useCompany.ts).
 *
 * The query is gated on authentication: an unauthenticated profile fetch 401s,
 * and the axios response interceptor (client.ts) hard-redirects to /auth/login on
 * a 401, so firing it pre-auth would bounce the user out. Login's silentAuth primes
 * the cache imperatively (queryClient.fetchQuery) before flipping `authenticated`,
 * so by the time this activates it is a cache read, not a network round-trip.
 *
 * `meta.persist` makes the profile survive to localStorage so a cold boot has it
 * instantly; `refetchOnMount: true` then revalidates it if the persisted copy is
 * stale (it overrides the global `refetchOnMount: false`).
 */
export function useProfileQuery() {
  const { authenticated } = useAuthState();
  return useGetUserProfileUserProfileGet(undefined, {
    query: {
      enabled: authenticated,
      staleTime: PROFILE_STALE_TIME,
      ...PERSISTED_QUERY_OPTIONS,
    },
  });
}

export function useProfile(): User | undefined {
  return useProfileQuery().data?.data;
}
